import React, { useMemo, useState } from 'react';
import { ChartFilter } from '../ChartComponents';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { SelectedItemProps } from '../MattersByStatusChart';
import {
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    createStyles,
    makeStyles,
} from '@material-ui/core';
import { ChartInfo } from '../../components/ChartInfo';
import MTooltip from '@material-ui/core/Tooltip';
import { VideoModal } from '../../components/VideoModal';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import CloseIcon from '@material-ui/icons/Close';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import { RV_CHART_CLASS } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { formatNumber } from '../../helpers/ChartHelper';
import clsx from 'clsx';
import { SearchCriteria } from '../SearchCriteria';
import { DrillDownType } from '../DrillDownType';
import { useCurrencySymbol } from '../../hooks/useCurrencySymbol';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        rootLoader: {
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
        },
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        learnIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 15px 15px',
        },
        title: {
            fontSize: '2.5rem',
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        barContainer: {
            padding: '15px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            gap: '10px',
        },
        barContainerCol: {
            display: 'flex',
            gap: '10px',
            flexFlow: 'row wrap',
        },
        barContainerColContent: {
            padding: '10px 14px 6px 14px',
            flex: 1,
            borderRadius: '10px',
            boxShadow: 'none',
            border: '1px solid var(--rv-chart-line)',
            textAlign: 'left',
            '& h3': {
                margin: '4px 0 2px 0',
                fontSize: '13px',
                fontWeight: 800,
                color: 'var(--rv-chart-ink)',
            },
        },
        pointer: {
            cursor: 'pointer',
        },
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        table: {
            '& .MuiTableCell-root': {
                padding: '9px 10px',
                fontSize: '13px',
                color: 'var(--rv-chart-ink)',
                borderBottom: '1px solid var(--rv-chart-line)',
                fontVariantNumeric: 'tabular-nums',
            },
            '& .MuiTableCell-head': {
                fontSize: '11px',
                fontWeight: 800,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: 'var(--rv-chart-muted)',
            },
            '& .MuiTableCell-body:not(th)': {
                fontWeight: 700,
            },
            '& th.MuiTableCell-body': {
                fontWeight: 600,
                color: 'var(--rv-chart-muted)',
            },
            '& .MuiTableBody-root .MuiTableRow-root': {
                transition: 'background-color 0.15s ease',
            },
            '& .MuiTableBody-root .MuiTableRow-root:hover': {
                backgroundColor: '#fafbff',
            },
            '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                borderBottom: 'none',
            },
        },
        tableHeaderCell: {
            fontWeight: 800,
        },
        bottomTableContainer: {
            maxWidth: '800px',
            margin: '0 auto',
        }
    }),
);

type KPISummaryDataType = 'New Matters' | 'Non-Billable Hours' | 'Billable Hours' | 'Fees Recorded' | 'Fees Billed' | 'Fees Collected';

type KPISummaryToDateType = {
    actual: number | string | null;
    budget: number | string | null;
    percentage: number | string | null;
    dot?: 'red' | 'orange' | 'green';
};

type KPISummaryData = {
    name: KPISummaryDataType;
    weekToDate: KPISummaryToDateType;
    monthToDate: KPISummaryToDateType;
    yearToDate: KPISummaryToDateType;
    previousMonthToDate?: KPISummaryToDateType;
    previousMonthYearToDate?: KPISummaryToDateType;
};

type KPISummaryBottomData = {
    name: 'Total Debt' | 'Unbilled Fees' | 'Unbilled Disbursements';
    data: Total | null;
    exceedPercentage: number | null;
};

type DateOption = keyof Omit<KPISummaryData, 'name'>;
interface KPISummaryExpandedChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    infoUrl: string | null;
}

export const KPISummaryExpandedChart = (props: KPISummaryExpandedChartProps) => {
    const classes = useStyles();
    // tslint:disable-next-line: no-any
    const entireChartRef = React.useRef<any>();

    const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);

    const kpiSummaryQuery = useQuery<KpiSummaryData, KpiSummaryDataParams>(KPISummaryQuery, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility
        }
    });

    const [data, setData] = useState<KPISummaryData[]>([]);

    const [bottomData, setBottomData] = useState<KPISummaryBottomData[]>([]);

    const [currencySymbol, setCurrencySymbol] = useState<string>('$');

    const currencyQuery = useCurrencySymbol();

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0,
    })[1];

    const  getIndicator = (percentage: number, amberIndicatorWhenActualVsBudgetIsLessThanPercentage: number | null, redIndicatorWhenActualVsBudgetIsLessThanPercentage: number | null) => {
        if (!percentage || Math.round(percentage) === 0 || !amberIndicatorWhenActualVsBudgetIsLessThanPercentage || !redIndicatorWhenActualVsBudgetIsLessThanPercentage) {
            return undefined;
        }
        if (Math.round(percentage) <= redIndicatorWhenActualVsBudgetIsLessThanPercentage) {
            return 'red';
        } else if (Math.round(percentage) < amberIndicatorWhenActualVsBudgetIsLessThanPercentage) {
            return 'orange';
        } else {
            return 'green'
        }
    };

    useMemo(() => {
        if (!currencyQuery.loading && !currencyQuery.error && currencyQuery.data) {
            setCurrencySymbol(currencyQuery.data.options.currencySymbol.symbol);
        }
    // tslint:disable-next-line: align
    }, [currencyQuery.loading, currencyQuery.data, currencyQuery.error]);

    useMemo(() => {
        if (!kpiSummaryQuery.loading && !kpiSummaryQuery.error && kpiSummaryQuery.data) {

            const {
                amberIndicatorWhenActualVsBudgetIsLessThanPercentage,
                redIndicatorWhenActualVsBudgetIsLessThanPercentage,
                redIndicatorWhenNinetyDayDebtExceedsPercentage,
                redIndicatorWhenNinetyDayUnbilledDisbursementsExceedsPercentage,
                redIndicatorWhenNinetyDayUnbilledFeesExceedsPercentage
            } = kpiSummaryQuery.data.settings.systems.financialThresholdsSettings.config

            const {
                newMattersMTD,
                newMattersYTD,
                nonBillableHoursMTD,
                nonBillableHoursYTD,
                billableHoursMTD,
                billableHoursYTD,
                feesRecordedMTD,
                feesRecordedYTD,
                feesBilledMTD,
                feesBilledYTD,
                feesCollectedMTD,
                feesCollectedYTD,
                billableHoursPreviousMTD,
                billableHoursPreviousMYTD,
                feesRecordedPreviousMTD,
                feesBilledPreviousMTD,
                feesBilledPreviousMYTD,
                feesCollectedPreviousMTD,
                feesCollectedPreviousMYTD,
                feesRecordedPreviousMYTD,
                newMattersPreviousMTD,
                newMattersPreviousMYTD,
                nonBillableHoursPreviousMTD,
                nonBillableHoursPreviousMYTD,
                totalDebtOwing,
                totalUnbilledDisbursements,
                totalUnbilledFees,
                billableHoursWTD,
                feesBilledWTD,
                feesRecordedWTD,
                feesCollectedWTD,
                newMattersWTD,
                nonBillableHoursWTD,
            } = kpiSummaryQuery.data.statistics.fees.kpiSummary;
            const tempData: KPISummaryData[] = [];
            const tempBottomData: KPISummaryBottomData[] = [];

            // bottom data
            if (totalDebtOwing) {
                tempBottomData.push({
                    name: 'Total Debt',
                    data: totalDebtOwing,
                    exceedPercentage: redIndicatorWhenNinetyDayDebtExceedsPercentage
                });
            }

            if (totalUnbilledFees) {
                tempBottomData.push({
                    name: 'Unbilled Fees',
                    data: totalUnbilledFees,
                    exceedPercentage: redIndicatorWhenNinetyDayUnbilledFeesExceedsPercentage
                });
            }

            if (totalUnbilledDisbursements) {
                tempBottomData.push({
                    name: 'Unbilled Disbursements',
                    data: totalUnbilledDisbursements,
                    exceedPercentage: redIndicatorWhenNinetyDayUnbilledDisbursementsExceedsPercentage
                });
            }

            // new matters
            tempData.push({
                name: 'New Matters',
                weekToDate: {
                    actual: newMattersWTD.periodTotal && newMattersWTD.periodTotal > 0 ? formatNumber(newMattersWTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                monthToDate: {
                    actual: newMattersMTD.periodTotal && newMattersMTD.periodTotal > 0 ? formatNumber(newMattersMTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                yearToDate: {
                    actual: newMattersYTD.periodTotal && newMattersYTD.periodTotal > 0 ? formatNumber(newMattersYTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                previousMonthToDate: {
                    actual: newMattersPreviousMTD.periodTotal && newMattersPreviousMTD.periodTotal > 0 ? formatNumber(newMattersPreviousMTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                previousMonthYearToDate: {
                    actual: newMattersPreviousMYTD.periodTotal && newMattersPreviousMYTD.periodTotal > 0 ? formatNumber(newMattersPreviousMYTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                }
            });

            // billable hours add dollar to acutal and budget
            tempData.push({
                name: 'Billable Hours',
                weekToDate: {
                    actual: billableHoursWTD.periodTotal && billableHoursWTD.periodTotal > 0 ? `${formatNumber(billableHoursWTD.periodTotal)}` : null,
                    budget: billableHoursWTD.budget && billableHoursWTD.budget > 0 ? `${formatNumber(billableHoursWTD.budget)}` : null,
                    percentage: billableHoursWTD.percentage && billableHoursWTD.percentage > 0 ? `${formatNumber(billableHoursWTD.percentage)}%` : null,
                    dot: getIndicator(billableHoursWTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                monthToDate: {
                    actual: billableHoursMTD.periodTotal && billableHoursMTD.periodTotal > 0 ? `${formatNumber(billableHoursMTD.periodTotal)}` : null,
                    budget: billableHoursMTD.budget && billableHoursMTD.budget > 0 ? `${formatNumber(billableHoursMTD.budget)}` : null,
                    percentage: billableHoursMTD.percentage && billableHoursMTD.percentage > 0 ? `${formatNumber(billableHoursMTD.percentage)}%` : null,
                    dot: getIndicator(billableHoursMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                yearToDate: {
                    actual: billableHoursYTD.periodTotal && billableHoursYTD.periodTotal > 0 ? `${formatNumber(billableHoursYTD.periodTotal)}` : null,
                    budget: billableHoursYTD.budget && billableHoursYTD.budget > 0 ? `${formatNumber(billableHoursYTD.budget)}` : null,
                    percentage: billableHoursYTD.percentage && billableHoursYTD.percentage > 0 ? `${formatNumber(billableHoursYTD.percentage)}%` : null,
                    dot: getIndicator(billableHoursYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthToDate: {
                    actual: billableHoursPreviousMTD.periodTotal && billableHoursPreviousMTD.periodTotal > 0 ? `${formatNumber(billableHoursPreviousMTD.periodTotal)}` : null,
                    budget: billableHoursPreviousMTD.budget && billableHoursPreviousMTD.budget > 0 ? `${formatNumber(billableHoursPreviousMTD.budget)}` : null,
                    percentage: billableHoursPreviousMTD.percentage && billableHoursPreviousMTD.percentage > 0 ? `${formatNumber(billableHoursPreviousMTD.percentage)}%` : null,
                    dot: getIndicator(billableHoursPreviousMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthYearToDate: {
                    actual: billableHoursPreviousMYTD.periodTotal && billableHoursPreviousMYTD.periodTotal > 0 ? `${formatNumber(billableHoursPreviousMYTD.periodTotal)}` : null,
                    budget: billableHoursPreviousMYTD.budget && billableHoursPreviousMYTD.budget > 0 ? `${formatNumber(billableHoursPreviousMYTD.budget)}` : null,
                    percentage: billableHoursPreviousMYTD.percentage && billableHoursPreviousMYTD.percentage > 0 ? `${formatNumber(billableHoursPreviousMYTD.percentage)}%` : null,
                    dot: getIndicator(billableHoursPreviousMYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                }
            });

            // // fees recorded
            tempData.push({
                name: 'Fees Recorded',
                weekToDate: {
                    actual: feesRecordedWTD.periodTotal && feesRecordedWTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesRecordedWTD.periodTotal)}` : null,
                    budget: feesRecordedWTD.budget && feesRecordedWTD.budget > 0 ? `${currencySymbol}${formatNumber(feesRecordedWTD.budget)}` : null,
                    percentage: feesRecordedWTD.percentage && feesRecordedWTD.percentage > 0 ? `${formatNumber(feesRecordedWTD.percentage)}%` : null,
                    dot: getIndicator(feesRecordedWTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                monthToDate: {
                    actual: feesRecordedMTD.periodTotal && feesRecordedMTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesRecordedMTD.periodTotal)}` : null,
                    budget: feesRecordedMTD.budget && feesRecordedMTD.budget > 0 ? `${currencySymbol}${formatNumber(feesRecordedMTD.budget)}` : null,
                    percentage: feesRecordedMTD.percentage && feesRecordedMTD.percentage > 0 ? `${formatNumber(feesRecordedMTD.percentage)}%` : null,
                    dot: getIndicator(feesRecordedMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                yearToDate: {
                    actual: feesRecordedYTD.periodTotal && feesRecordedYTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesRecordedYTD.periodTotal)}` : null,
                    budget: feesRecordedYTD.budget && feesRecordedYTD.budget > 0 ? `${currencySymbol}${formatNumber(feesRecordedYTD.budget)}` : null,
                    percentage: feesRecordedYTD.percentage && feesRecordedYTD.percentage > 0 ? `${formatNumber(feesRecordedYTD.percentage)}%` : null,
                    dot: getIndicator(feesRecordedYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthToDate: {
                    actual:
                        feesRecordedPreviousMTD.periodTotal && feesRecordedPreviousMTD.periodTotal > 0
                            ? `${currencySymbol}${formatNumber(feesRecordedPreviousMTD.periodTotal)}`
                            : null,
                    budget: feesRecordedPreviousMTD.budget && feesRecordedPreviousMTD.budget > 0 ? `${currencySymbol}${formatNumber(feesRecordedPreviousMTD.budget)}` : null,
                    percentage: feesRecordedPreviousMTD.percentage && feesRecordedPreviousMTD.percentage > 0 ? `${formatNumber(feesRecordedPreviousMTD.percentage)}%` : null,
                    dot: getIndicator(feesRecordedPreviousMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthYearToDate: {
                    actual:
                        feesRecordedPreviousMYTD.periodTotal && feesRecordedPreviousMYTD.periodTotal > 0
                            ? `${currencySymbol}${formatNumber(feesRecordedPreviousMYTD.periodTotal)}`
                            : null,
                    budget: feesRecordedPreviousMYTD.budget && feesRecordedPreviousMYTD.budget > 0 ? `${currencySymbol}${formatNumber(feesRecordedPreviousMYTD.budget)}` : null,
                    percentage: feesRecordedPreviousMYTD.percentage && feesRecordedPreviousMYTD.percentage > 0 ? `${formatNumber(feesRecordedPreviousMYTD.percentage)}%` : null,
                    dot: getIndicator(feesRecordedPreviousMYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                }
            });

            // fees billed
            tempData.push({
                name: 'Fees Billed',
                monthToDate: {
                    actual: feesBilledMTD.periodTotal && feesBilledMTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesBilledMTD.periodTotal)}` : null,
                    budget: feesBilledMTD.budget && feesBilledMTD.budget > 0 ? `${currencySymbol}${formatNumber(feesBilledMTD.budget)}` : null,
                    percentage: feesBilledMTD.percentage && feesBilledMTD.percentage > 0 ? `${formatNumber(feesBilledMTD.percentage)}%` : null,
                    dot: getIndicator(feesBilledMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                yearToDate: {
                    actual: feesBilledYTD.periodTotal && feesBilledYTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesBilledYTD.periodTotal)}` : null,
                    budget: feesBilledYTD.budget && feesBilledYTD.budget > 0 ? `${currencySymbol}${formatNumber(feesBilledYTD.budget)}` : null,
                    percentage: feesBilledYTD.percentage && feesBilledYTD.percentage > 0 ? `${formatNumber(feesBilledYTD.percentage)}%` : null,
                    dot: getIndicator(feesBilledYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthToDate: {
                    actual:
                        feesBilledPreviousMTD.periodTotal && feesBilledPreviousMTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesBilledPreviousMTD.periodTotal)}` : null,
                    budget: feesBilledPreviousMTD.budget && feesBilledPreviousMTD.budget > 0 ? `${currencySymbol}${formatNumber(feesBilledPreviousMTD.budget)}` : null,
                    percentage: feesBilledPreviousMTD.percentage && feesBilledPreviousMTD.percentage > 0 ? `${formatNumber(feesBilledPreviousMTD.percentage)}%` : null,
                    dot: getIndicator(feesBilledPreviousMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthYearToDate: {
                    actual:
                        feesBilledPreviousMYTD.periodTotal && feesBilledPreviousMYTD.periodTotal > 0
                            ? `${currencySymbol}${formatNumber(feesBilledPreviousMYTD.periodTotal)}`
                            : null,
                    budget: feesBilledPreviousMYTD.budget && feesBilledPreviousMYTD.budget > 0 ? `${currencySymbol}${formatNumber(feesBilledPreviousMYTD.budget)}` : null,
                    percentage: feesBilledPreviousMYTD.percentage && feesBilledPreviousMYTD.percentage > 0 ? `${formatNumber(feesBilledPreviousMYTD.percentage)}%` : null,
                    dot: getIndicator(feesBilledPreviousMYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                weekToDate: {
                    actual: feesBilledWTD.periodTotal && feesBilledWTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesBilledWTD.periodTotal)}` : null,
                    budget: feesBilledWTD.budget && feesBilledWTD.budget > 0 ? `${currencySymbol}${formatNumber(feesBilledWTD.budget)}` : null,
                    percentage: feesBilledWTD.percentage && feesBilledWTD.percentage > 0 ? `${formatNumber(feesBilledWTD.percentage)}%` : null,
                    dot: getIndicator(feesBilledWTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
            });

            // fees collected
            tempData.push({
                name: 'Fees Collected',
                weekToDate: {
                    actual: feesCollectedWTD.periodTotal && feesCollectedWTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesCollectedWTD.periodTotal)}` : null,
                    budget: feesCollectedWTD.budget && feesCollectedWTD.budget > 0 ? `${currencySymbol}${formatNumber(feesCollectedWTD.budget)}` : null,
                    percentage: feesCollectedWTD.percentage && feesCollectedWTD.percentage > 0 ? `${formatNumber(feesCollectedWTD.percentage)}%` : null,
                    dot: getIndicator(feesCollectedWTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                monthToDate: {
                    actual: feesCollectedMTD.periodTotal && feesCollectedMTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesCollectedMTD.periodTotal)}` : null,
                    budget: feesCollectedMTD.budget && feesCollectedMTD.budget > 0 ? `${currencySymbol}${formatNumber(feesCollectedMTD.budget)}` : null,
                    percentage: feesCollectedMTD.percentage && feesCollectedMTD.percentage > 0 ? `${formatNumber(feesCollectedMTD.percentage)}%` : null,
                    dot: getIndicator(feesCollectedMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                yearToDate: {
                    actual: feesCollectedYTD.periodTotal && feesCollectedYTD.periodTotal > 0 ? `${currencySymbol}${formatNumber(feesCollectedYTD.periodTotal)}` : null,
                    budget: feesCollectedYTD.budget && feesCollectedYTD.budget > 0 ? `${currencySymbol}${formatNumber(feesCollectedYTD.budget)}` : null,
                    percentage: feesCollectedYTD.percentage && feesCollectedYTD.percentage > 0 ? `${formatNumber(feesCollectedYTD.percentage)}%` : null,
                    dot: getIndicator(feesCollectedYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthToDate: {
                    actual:
                        feesCollectedPreviousMTD.periodTotal && feesCollectedPreviousMTD.periodTotal > 0
                            ? `${currencySymbol}${formatNumber(feesCollectedPreviousMTD.periodTotal)}`
                            : null,
                    budget: feesCollectedPreviousMTD.budget && feesCollectedPreviousMTD.budget > 0 ? `${currencySymbol}${formatNumber(feesCollectedPreviousMTD.budget)}` : null,
                    percentage: feesCollectedPreviousMTD.percentage && feesCollectedPreviousMTD.percentage > 0 ? `${formatNumber(feesCollectedPreviousMTD.percentage)}%` : null,
                    dot: getIndicator(feesCollectedPreviousMTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
                previousMonthYearToDate: {
                    actual:
                        feesCollectedPreviousMYTD.periodTotal && feesCollectedPreviousMYTD.periodTotal > 0
                            ? `${currencySymbol}${formatNumber(feesCollectedPreviousMYTD.periodTotal)}`
                            : null,
                    budget: feesCollectedPreviousMYTD.budget && feesCollectedPreviousMYTD.budget > 0 ? `${currencySymbol}${formatNumber(feesCollectedPreviousMYTD.budget)}` : null,
                    percentage: feesCollectedPreviousMYTD.percentage && feesCollectedPreviousMYTD.percentage > 0 ? `${formatNumber(feesCollectedPreviousMYTD.percentage)}%` : null,
                    dot: getIndicator(feesCollectedPreviousMYTD.percentage, amberIndicatorWhenActualVsBudgetIsLessThanPercentage, redIndicatorWhenActualVsBudgetIsLessThanPercentage)
                },
            });

            // non-billable hours
            tempData.push({
                name: 'Non-Billable Hours',
                weekToDate: {
                    actual: nonBillableHoursWTD.periodTotal && nonBillableHoursWTD.periodTotal > 0 ? formatNumber(nonBillableHoursWTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                monthToDate: {
                    actual: nonBillableHoursMTD.periodTotal && nonBillableHoursMTD.periodTotal > 0 ? formatNumber(nonBillableHoursMTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                yearToDate: {
                    actual: nonBillableHoursYTD.periodTotal && nonBillableHoursYTD.periodTotal > 0 ? formatNumber(nonBillableHoursYTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                previousMonthToDate: {
                    actual: nonBillableHoursPreviousMTD.periodTotal && nonBillableHoursPreviousMTD.periodTotal > 0 ? formatNumber(nonBillableHoursPreviousMTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
                previousMonthYearToDate: {
                    actual:
                        nonBillableHoursPreviousMYTD.periodTotal && nonBillableHoursPreviousMYTD.periodTotal > 0 ? formatNumber(nonBillableHoursPreviousMYTD.periodTotal) : null,
                    budget: null,
                    percentage: null,
                },
            });

            setData(tempData);
            setBottomData(tempBottomData);
        }
    // tslint:disable-next-line: align
    }, [kpiSummaryQuery.loading, kpiSummaryQuery.error, kpiSummaryQuery.data, currencySymbol]);

    const onToggleVideo = () => {
        setIsVideoModalOpen(!isVideoModalOpen);
    };

    const onMaximize = () => {
        if (props.onMaximize) {
            props.onMaximize();
        }
    };

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart(`isKpiSummaryOpen`);
        }
    };

    const onDownload =
        (name: string, extension: string = 'jpg') =>
        // tslint:disable-next-line: no-any
        (image: any) => {
            const a = document.createElement('a');
            a.href = image;
            a.download = createFileName(extension, name);
            a.click();
        };

    // tslint:disable-next-line: no-any
    const onDownloadScreenshot = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload(`KPI Summary`));
            // tslint:disable-next-line: align
        }, 500);
    };

    const getPeriodTitle = (period: DateOption) => {
        switch (period) {
            case 'weekToDate':
                return 'Week to Date';
            case 'monthToDate':
                return 'Month to Date';
            case 'previousMonthToDate':
                return 'Previous Month';
            case 'yearToDate':
                return 'Year to Date';
            case 'previousMonthYearToDate':
                return 'Previous Month YTD';
            default:
                return 'Month to Date';
        }
    };

    const getNewMattersCriteria = (period: DateOption): SearchCriteria[] => {
        switch (period) {
            case 'weekToDate':
                return [{
                    comparison: 'curr',
                    operator: 'instruction',
                    property: 'WeekToDate',
                }];
            case 'monthToDate':
                return [{
                    comparison: 'curr',
                    operator: 'instruction',
                    property: 'MonthToDate',
                }];
            case 'previousMonthToDate':
                return [{
                    comparison: 'prev',
                    operator: 'instruction',
                    property: 'ThisMonth',
                }];
            case 'yearToDate':
                return [{
                    comparison: 'curr',
                    operator: 'instruction',
                    property: 'YearToDate',
                }];
            case 'previousMonthYearToDate':
                return [{
                    comparison: 'pmytd',
                    operator: 'instruction',
                    property: 'MonthToDate',
                }];
            default:
                return [{
                    comparison: 'curr',
                    operator: 'instruction',
                    property: 'MonthToDate',
                }];
        }
    };

    const getNonBillableHoursCriteria = (period: DateOption): SearchCriteria[] => {
        switch (period) {
            case 'weekToDate':
                return [
                    { property: 'WeekToDate', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'eq', comparison: '0' }
                ];
            case 'monthToDate':
                return [
                    { property: 'ThisMonth', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'eq', comparison: '0' }
                ];
            case 'previousMonthToDate':
                return [
                    {property: 'Amount', operator: 'eq', comparison: '0'},
                    {property: 'ThisMonth', operator: '', comparison: 'prev'}
                ];
            case 'yearToDate':
                return [
                    { property: 'FinancialYear', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'eq', comparison: '0' }
                ];
            case 'previousMonthYearToDate':
                return [
                    {property: 'Amount', operator: 'eq', comparison: '0'},
                    {property: 'FinancialYear', operator: '', comparison: 'prevMonth'}
                ];
            default:
                return [
                    { property: 'ThisMonth', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'eq', comparison: '0' }
                ];
        }
    };

    const getBillableHoursCriteria = (period: DateOption): SearchCriteria[] => {
        switch (period) {
            case 'weekToDate':
                return [
                    { property: 'WeekToDate', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'gt', comparison: '0' }
                ];
            case 'monthToDate':
                return [
                    { property: 'MonthToDate', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'gt', comparison: '0' }
                ];
            case 'previousMonthToDate':
                return [{ property: 'MonthToDate', operator: '', comparison: 'prev'}, { property: 'Amount', operator: 'gt', comparison: '0'}];
            case 'yearToDate':
                return [
                    { property: 'FinancialYear', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'gt', comparison: '0' }
                ];
            case 'previousMonthYearToDate':
                return [{property: 'FinancialYear', operator: '', comparison: 'prevMonth'}, { property: 'Amount', operator: 'gt', comparison: '0'}];
            default:
                return [
                    { property: 'MonthToDate', operator: '', comparison: 'curr' },
                    { property: 'Amount', operator: 'gt', comparison: '0' }
                ];
        }
    };

    const getFeesRecordedCriteria = (period: DateOption): SearchCriteria[] => {
        switch (period) {
            case 'weekToDate':
                return [{ property: 'Week', operator: '', comparison: 'curr', period: '' }];
            case 'monthToDate':
                return [{ property: 'Month', operator: '', comparison: 'curr', period: '' }];
            case 'previousMonthToDate':
                return [{property: 'Month', operator: '', comparison: 'prev', period: ''}];
            case 'yearToDate':
                return [{ property: 'FinancialYear', operator: '', comparison: 'curr', period: '' }];
            case 'previousMonthYearToDate':
                return [{property: 'FinancialYear', operator: '', comparison: 'curr', period: ''}];
            default:
                return [{ property: 'Month', operator: '', comparison: 'curr', period: '' }];
        }
    };

    const getFeesBilledCriteria = (period: DateOption): SearchCriteria[] => {
        switch (period) {
            case 'weekToDate':
                return [{ property: 'Week', operator: '', comparison: '', period: 'curr' }];
            case 'monthToDate':
                return [{ property: 'Month', operator: '', comparison: '', period: 'curr' }];
            case 'previousMonthToDate':
                return [{property: 'Month', operator: '', comparison: '', period: 'prev'}];
            case 'yearToDate':
                return [{ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr' }];
            case 'previousMonthYearToDate':
                return [{property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr'}];
            default:
                return [{ property: 'Month', operator: '', comparison: '', period: 'curr' }];
        }
    };

    const getFeesCollectedCriteria = (period: DateOption): SearchCriteria[] => {
        switch (period) {
            case 'weekToDate':
                return [{ property: 'Week', operator: '', comparison: '', period: 'curr' }];
            case 'monthToDate':
                return [{ property: 'Month', operator: '', comparison: '', period: 'curr' }];
            case 'previousMonthToDate':
                return [{property: 'Month', operator: '', comparison: '', period: 'prev'}];
            case 'yearToDate':
                return [{ property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr' }];
            case 'previousMonthYearToDate':
                return [{property: 'FinancialYearToDate', operator: '', comparison: '', period: 'curr'}];
            default:
                return [{ property: 'Month', operator: '', comparison: '', period: 'curr' }];
        }
    };

    const onTotalDrilldown = (item: KPISummaryBottomData) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            let title = `KPI Summary - ${item.name}`;
            let drillDownType = -1;
            let defaultView = '';

            if (item.name === 'Total Debt') {
                searchCriterias.push({property: 'DebtOwing', operator: 'not', comparison: '0'});
                searchCriterias.push({property: 'DebtOwing', operator: 'isnot', comparison: 'null'});
                drillDownType = DrillDownType.matterSummaryDrilldown;
                defaultView = 'Debtors';
            } else if (item.name === 'Unbilled Fees') {
                searchCriterias.push({property: 'TotalWIPBalanceExTax', operator: 'not', comparison: '0'});
                searchCriterias.push({property: 'TotalWIPBalanceExTax', operator: 'isnot', comparison: 'null'});
                drillDownType = DrillDownType.matterSummaryDrilldown;
                defaultView = 'Unbilled Fees';
            } else if (item.name === 'Unbilled Disbursements') {
                searchCriterias.push({property: 'UnbilledDisbursements', operator: 'gt', comparison: '0'});
                drillDownType = DrillDownType.matterSummaryDrilldown;
                defaultView = 'Matters';
            }

            props.getSelectedItem(searchCriterias, true, title, drillDownType, defaultView);
        }
    };

    const onDrilldown = (name: KPISummaryDataType, period: DateOption, type: keyof KPISummaryToDateType) => (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            let title = '';
            let drillDownType = -1;
            let defaultView = '';
            
            if (name === 'New Matters') {
                // title = `KPI Summary - New Matters ${period === 'YearToDate' ? 'YTD' : 'MTD'} ${capitalizeFirstLetter(type)}`;
                title = `KPI Summary - New Matters for ${getPeriodTitle(period)}`;

                searchCriterias.push(...getNewMattersCriteria(period));
                
                drillDownType = DrillDownType.matterSummaryDrilldown;

                defaultView = 'New Matter Details';

            } else if (name === 'Non-Billable Hours') {
                // title = `KPI Summary - Non-Billable Hours ${period === 'YearToDate' ? 'YTD' : 'MTD'} ${capitalizeFirstLetter(type)}`;
                title = `Drilldown from KPI Summary - Non-Billable for ${getPeriodTitle(period)}`;
                searchCriterias.push(...getNonBillableHoursCriteria(period));
                drillDownType = DrillDownType.timesheetSummary;
                defaultView = 'Timesheet Lines';

            } else if (name === 'Billable Hours') {
                // title = `KPI Summary - Billable Hours ${period === 'YearToDate' ? 'YTD' : 'MTD'} ${capitalizeFirstLetter(type)}`;
                title = `Drilldown from KPI Summary - Billable Hours for ${getPeriodTitle(period)}`;
                searchCriterias.push(...getBillableHoursCriteria(period));
                drillDownType = DrillDownType.timesheetSummary;
                defaultView = 'Timesheet Lines';

            } else if (name === 'Fees Recorded') {
                // title = `KPI Summary - Fees Recorded ${period === 'YearToDate' ? 'YTD' : 'MTD'} ${capitalizeFirstLetter(type)}`;
                title = `Drilldown from KPI Summary - Fees Recorded for ${getPeriodTitle(period)}`;
                searchCriterias.push(...getFeesRecordedCriteria(period));
                drillDownType = DrillDownType.staffBillingSummary;
                defaultView = period === 'monthToDate' || period === 'yearToDate' ? 'Fees Produced MTD/YTD' : 'Fees Produced PM/PYTD';

            } else if (name === 'Fees Billed') {
                // title = `KPI Summary - Fees Billed ${period === 'YearToDate' ? 'YTD' : 'MTD'} ${capitalizeFirstLetter(type)}`;
                title = `Drilldown from KPI Summary - Fees Billed for ${getPeriodTitle(period)}`;
                searchCriterias.push(...getFeesBilledCriteria(period));
                drillDownType = DrillDownType.staffBillingSummary;
                defaultView = period === 'monthToDate' || period === 'yearToDate' ? 'Fees Billed MTD/YTD' : 'Fees Billed PM/PYTD';

            } else if (name === 'Fees Collected') {
                // title = `KPI Summary - Fees Collected ${period === 'YearToDate' ? 'YTD' : 'MTD'} ${capitalizeFirstLetter(type)}`;
                title = `Drilldown from KPI Summary - Fees Collected for ${getPeriodTitle(period)}`;
                searchCriterias.push(...getFeesCollectedCriteria(period));
                drillDownType = DrillDownType.staffBillingSummary;
                defaultView = period === 'monthToDate' || period === 'yearToDate' ? 'Fees Collected MTD/YTD' : 'Fees Collected PM/PYTD';
            }
            props.getSelectedItem(searchCriterias, true, title, drillDownType, defaultView);
        }
        
    };

    const chartControlComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <div className={`${classes.helpIcon} rv-chart-help`}>
                        <ChartInfo
                            description={props.description}
                            fontSize={props.showAllRecords ? 'large' : 'medium'}
                            // className={classes.helpIcon}
                        />
                        {props.infoUrl && (
                            <MTooltip title="Learn more">
                                <IconButton className={classes.learnIcon} aria-label="maximize" onClick={onToggleVideo} size={props.showAllRecords ? 'medium' : 'small'}>
                                    <VideoLibraryOutlinedIcon />
                                </IconButton>
                            </MTooltip>
                        )}
                    </div>
                    {(props.showAllRecords || props.showScreenshot) && (
                        <IconButton 
                            size={props.showAllRecords ? 'medium' : 'small'}
                            onClick={onDownloadScreenshot}
                        >
                            <GetAppOutlinedIcon 
                                color="primary" 
                                fontSize={props.showAllRecords ? 'large' : 'medium'}
                            />
                        </IconButton>
                    )}
                    {!props.showAllRecords && props.onMaximize && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    if (kpiSummaryQuery.loading || currencyQuery.loading) {
        return (
            <div className={`${classes.rootLoader} ${RV_CHART_CLASS}`}>
                {chartControlComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}`} ref={entireChartRef}>
            {isVideoModalOpen && props.infoUrl && <VideoModal open={isVideoModalOpen} onClose={onToggleVideo} url={props.infoUrl} />}
            {chartControlComponent()}
            <div className={classes.titleContainer}>
                <ChartHeading icon={<AssessmentOutlinedIcon />} title={"KPI Summary"} />
            </div>
            <div className={classes.barContainer}>
                <div className={classes.barContainerCol}>
                    
                    <Paper elevation={3} className={classes.barContainerColContent}>
                        <h3>Week to Date</h3>
                        <TableContainer>
                            <Table className={classes.table} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell rowSpan={1} />
                                        <TableCell colSpan={3} align="center" className={classes.tableHeaderCell}>Week to Date</TableCell>
                                    </TableRow>
                                    <TableRow >
                                        <TableCell />
                                        <TableCell align="right" className={classes.tableHeaderCell}>Actual</TableCell>
                                        <TableCell align="right" className={classes.tableHeaderCell}>Budget</TableCell>
                                        <TableCell align="center" className={classes.tableHeaderCell}>% Achieved</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((item, index) => {

                                        // const tempItem = item[options] ?? item.monthToDate;

                                        return (
                                            <TableRow key={index}>
                                                <TableCell 
                                                    component="th" 
                                                    scope="row"
                                                >
                                                    {item.name}
                                                </TableCell>
                                                <TableCell 
                                                    align="right"
                                                    onClick={item.weekToDate.actual ? onDrilldown(item.name, 'weekToDate', 'actual') : undefined}
                                                    className={clsx(item.weekToDate.actual ? classes.pointer : '')}
                                                >
                                                    {item.weekToDate.actual}
                                                </TableCell>
                                                <TableCell 
                                                    align="right"
                                                    onClick={item.weekToDate.budget ? onDrilldown(item.name, 'weekToDate', 'budget') : undefined}
                                                    className={clsx(item.weekToDate.budget ? classes.pointer : '')}
                                                >
                                                    {item.weekToDate.budget}
                                                </TableCell>
                                                <TableCell 
                                                    align="center"
                                                    onClick={item.weekToDate.percentage ? onDrilldown(item.name, 'weekToDate', 'percentage') : undefined}
                                                    className={clsx(item.weekToDate.percentage ? classes.pointer : '')}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-end',
                                                            gap: '5px'
                                                        }}
                                                    >
                                                        <div>{item.weekToDate.percentage}</div>
                                                        
                                                        <div 
                                                            style={{
                                                                width: '10px',
                                                                height: '10px',
                                                                borderRadius: '100%',
                                                                backgroundColor: item.weekToDate.dot
                                                            }}
                                                        />
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                    <Paper elevation={3} className={classes.barContainerColContent}>
                        <h3>Current Month</h3>
                        <TableContainer>
                            <Table className={classes.table} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell rowSpan={1} />
                                        <TableCell colSpan={3} align="center" className={classes.tableHeaderCell}>Month to Date</TableCell>
                                        <TableCell colSpan={3} align="center" className={classes.tableHeaderCell}>Year to Date</TableCell>
                                    </TableRow>
                                    <TableRow >
                                        <TableCell />
                                        <TableCell align="right" className={classes.tableHeaderCell}>Actual</TableCell>
                                        <TableCell align="right" className={classes.tableHeaderCell}>Budget</TableCell>
                                        <TableCell align="center" className={classes.tableHeaderCell}>% Achieved</TableCell>
                                        <TableCell align="right" className={classes.tableHeaderCell}>Actual</TableCell>
                                        <TableCell align="right" className={classes.tableHeaderCell}>Budget</TableCell>
                                        <TableCell align="center" className={classes.tableHeaderCell}>% Achieved</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((item, index) => {

                                        // const tempItem = item[options] ?? item.monthToDate;

                                        return (
                                            <TableRow key={index}>
                                                <TableCell 
                                                    component="th" 
                                                    scope="row"
                                                >
                                                    {item.name}
                                                </TableCell>
                                                <TableCell 
                                                    align="right"
                                                    onClick={item.monthToDate.actual ? onDrilldown(item.name, 'monthToDate', 'actual') : undefined}
                                                    className={clsx(item.monthToDate.actual ? classes.pointer : '')}
                                                >
                                                    {item.monthToDate.actual}
                                                </TableCell>
                                                <TableCell 
                                                    align="right"
                                                    onClick={item.monthToDate.budget ? onDrilldown(item.name, 'monthToDate', 'budget') : undefined}
                                                    className={clsx(item.monthToDate.budget ? classes.pointer : '')}
                                                >
                                                    {item.monthToDate.budget}
                                                </TableCell>
                                                <TableCell 
                                                    align="center"
                                                    onClick={item.monthToDate.percentage ? onDrilldown(item.name, 'monthToDate', 'percentage') : undefined}
                                                    className={clsx(item.monthToDate.percentage ? classes.pointer : '')}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-end',
                                                            gap: '5px'
                                                        }}
                                                    >
                                                        <div>{item.monthToDate.percentage}</div>
                                                        
                                                        <div 
                                                            style={{
                                                                width: '10px',
                                                                height: '10px',
                                                                borderRadius: '100%',
                                                                backgroundColor: item.monthToDate.dot
                                                            }}
                                                        />
                                                    </div>
                                                </TableCell>
                                                <TableCell 
                                                    align="right"
                                                    onClick={item.yearToDate.actual ? onDrilldown(item.name, 'yearToDate', 'actual') : undefined}
                                                    className={clsx(item.yearToDate.actual ? classes.pointer : '')}
                                                >
                                                    {item.yearToDate.actual}
                                                </TableCell>
                                                <TableCell 
                                                    align="right"
                                                    onClick={item.yearToDate.budget ? onDrilldown(item.name, 'yearToDate', 'budget') : undefined}
                                                    className={clsx(item.yearToDate.budget ? classes.pointer : '')}
                                                >
                                                    {item.yearToDate.budget}
                                                </TableCell>
                                                <TableCell 
                                                    align="center"
                                                    onClick={item.yearToDate.percentage ? onDrilldown(item.name, 'yearToDate', 'percentage') : undefined}
                                                    className={clsx(item.yearToDate.percentage ? classes.pointer : '')}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-end',
                                                            gap: '5px'
                                                        }}
                                                    >
                                                        <div>{item.yearToDate.percentage}</div>
                                                        
                                                        <div 
                                                            style={{
                                                                width: '10px',
                                                                height: '10px',
                                                                borderRadius: '100%',
                                                                backgroundColor: item.yearToDate.dot
                                                            }}
                                                        />
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                    <Paper elevation={3} className={classes.barContainerColContent}>
                        <h3>As At Previous Month End</h3>
                        <TableContainer>
                            <Table className={classes.table} aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell rowSpan={1} />
                                        <TableCell colSpan={3} align="center" className={classes.tableHeaderCell}>Month to Date</TableCell>
                                        <TableCell colSpan={3} align="center" className={classes.tableHeaderCell}>Year to Date</TableCell>
                                    </TableRow>
                                    <TableRow >
                                        <TableCell />
                                        <TableCell align="right" className={classes.tableHeaderCell}>Actual</TableCell>
                                        <TableCell align="right" className={classes.tableHeaderCell}>Budget</TableCell>
                                        <TableCell align="center" className={classes.tableHeaderCell}>% Achieved</TableCell>
                                        <TableCell align="right" className={classes.tableHeaderCell}>Actual</TableCell>
                                        <TableCell align="right" className={classes.tableHeaderCell}>Budget</TableCell>
                                        <TableCell align="center" className={classes.tableHeaderCell}>% Achieved</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((item, index) => {

                                        // const tempItem = item[options] ?? item.monthToDate;

                                        return (
                                            <TableRow key={index}>
                                                <TableCell 
                                                    component="th" 
                                                    scope="row"
                                                >
                                                    {item.name}
                                                </TableCell>
                                                <TableCell 
                                                    align="right"
                                                    onClick={item.previousMonthToDate?.actual ? onDrilldown(item.name, 'previousMonthToDate', 'actual') : undefined}
                                                    className={clsx(item.previousMonthToDate?.actual ? classes.pointer : '')}
                                                >
                                                    {item.previousMonthToDate?.actual}
                                                </TableCell>
                                                <TableCell 
                                                    align="right"
                                                    onClick={item.previousMonthToDate?.budget ? onDrilldown(item.name, 'previousMonthToDate', 'budget') : undefined}
                                                    className={clsx(item.previousMonthToDate?.budget ? classes.pointer : '')}
                                                >
                                                    {item.previousMonthToDate?.budget}
                                                </TableCell>
                                                <TableCell 
                                                    align="center"
                                                    onClick={item.previousMonthToDate?.percentage ? onDrilldown(item.name, 'previousMonthToDate', 'percentage') : undefined}
                                                    className={clsx(item.previousMonthToDate?.percentage ? classes.pointer : '')}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-end',
                                                            gap: '5px'
                                                        }}
                                                    >
                                                        <div>{item.previousMonthToDate?.percentage}</div>
                                                        
                                                        <div 
                                                            style={{
                                                                width: '10px',
                                                                height: '10px',
                                                                borderRadius: '100%',
                                                                backgroundColor: item.previousMonthToDate?.dot
                                                            }}
                                                        />
                                                    </div>
                                                </TableCell>
                                                <TableCell 
                                                    align="right"
                                                    onClick={item.previousMonthYearToDate?.actual ? onDrilldown(item.name, 'previousMonthYearToDate', 'actual') : undefined}
                                                    className={clsx(item.previousMonthYearToDate?.actual ? classes.pointer : '')}
                                                >
                                                    {item.previousMonthYearToDate?.actual}
                                                </TableCell>
                                                <TableCell 
                                                    align="right"
                                                    onClick={item.previousMonthYearToDate?.budget ? onDrilldown(item.name, 'previousMonthYearToDate', 'budget') : undefined}
                                                    className={clsx(item.previousMonthYearToDate?.budget ? classes.pointer : '')}
                                                >
                                                    {item.previousMonthYearToDate?.budget}
                                                </TableCell>
                                                <TableCell 
                                                    align="center"
                                                    onClick={item.previousMonthYearToDate?.percentage ? onDrilldown(item.name, 'previousMonthYearToDate', 'percentage') : undefined}
                                                    className={clsx(item.previousMonthYearToDate?.percentage ? classes.pointer : '')}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-end',
                                                            gap: '5px'
                                                        }}
                                                    >
                                                        <div>{item.previousMonthYearToDate?.percentage}</div>
                                                        
                                                        <div 
                                                            style={{
                                                                width: '10px',
                                                                height: '10px',
                                                                borderRadius: '100%',
                                                                backgroundColor: item.previousMonthYearToDate?.dot
                                                            }}
                                                        />
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </div>
                <Divider />
                <TableContainer
                    className={classes.bottomTableContainer}
                >
                    <Table className={classes.table} aria-label="simple table">
                        <TableBody>
                            {bottomData.map((item, index) => {

                                const { data: tempBottomData, exceedPercentage } = item;
                                return (
                                    <TableRow key={index}>
                                        <TableCell 
                                            component="th" 
                                            scope="row"
                                        >
                                            {item.name}
                                        </TableCell>
                                        <TableCell 
                                            align="right"
                                            onClick={onTotalDrilldown(item)}
                                            className={clsx(tempBottomData ? classes.pointer : '')}
                                        >
                                            {`${currencySymbol}${formatNumber(tempBottomData?.periodTotal ?? 0)}`}
                                        </TableCell>
                                        <TableCell 
                                            align="right"
                                            onClick={onTotalDrilldown(item)}
                                            className={clsx(tempBottomData ? classes.pointer : '')}
                                            style={{
                                                ...(tempBottomData && exceedPercentage && Math.round(tempBottomData.ninetyDaysPercentage) >= exceedPercentage ? {
                                                    color: 'red'
                                                } : {})
                                            }}
                                        >
                                            {`(${currencySymbol}${formatNumber(tempBottomData?.ninetyDaysTotal ?? 0)} or ${formatNumber(tempBottomData?.ninetyDaysPercentage ?? 0)}% is 90 days old or greater)`}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );    
};

type KpiSummaryDataParams = {
    entityType: number | undefined;
    entityGuid: string | undefined;
    responsibility: number | undefined;
};

type KpiSummaryData = {
    statistics: Statistics;
    settings: Settings;
};

type Settings = {
    id:         number;
    systems:    Systems;
    __typename: string;
}

type Systems = {
    financialThresholdsSettings: FinancialThresholdsSettings;
}

type FinancialThresholdsSettings = {
    id:         number;
    config:     Config;
}

type Config = {
    id:                                                              number | null;
    redIndicatorWhenActualVsBudgetIsLessThanPercentage:              number | null;
    amberIndicatorWhenActualVsBudgetIsLessThanPercentage:            number | null;
    redIndicatorWhenNinetyDayDebtExceedsPercentage:                  number | null;
    redIndicatorWhenNinetyDayUnbilledFeesExceedsPercentage:          number | null;
    redIndicatorWhenNinetyDayUnbilledDisbursementsExceedsPercentage: number | null;
    firmOverallStandardChargeOutRate:                                number | null;
    currentBankOverdraftInterestRate:                                number | null;
    firmStandardTradingTermsInDays:                                  number | null;
    firmTargetWipLockupDays:                                         number | null;
    showGreenIndicator:                                              boolean;
}

type Statistics = {
    fees: Fees;
};

type Fees = {
    kpiSummary: KpiSummary;
};

type KpiSummary = {
    newMattersYTD:               NewMattersMtd;
    newMattersMTD:               NewMattersMtd;
    newMattersWTD:               NewMattersMtd;
    newMattersPreviousMTD:       NewMattersMtd;
    newMattersPreviousMYTD:       NewMattersMtd;
    nonBillableHoursYTD:         NewMattersMtd;
    nonBillableHoursMTD:         NewMattersMtd;
    nonBillableHoursWTD:         NewMattersMtd;
    nonBillableHoursPreviousMTD: NewMattersMtd;
    nonBillableHoursPreviousMYTD: NewMattersMtd;
    billableHoursYTD:            BillableHoursMtd;
    billableHoursMTD:            BillableHoursMtd;
    billableHoursWTD:            BillableHoursMtd;
    billableHoursPreviousMTD:    BillableHoursMtd;
    billableHoursPreviousMYTD:    BillableHoursMtd;
    feesRecordedYTD:             BillableHoursMtd;
    feesRecordedMTD:             BillableHoursMtd;
    feesRecordedWTD:             BillableHoursMtd;
    feesRecordedPreviousMTD:     BillableHoursMtd;
    feesRecordedPreviousMYTD:     BillableHoursMtd;
    feesRecordedForecast:     BillableHoursMtd;
    feesBilledYTD:               BillableHoursMtd;
    feesBilledMTD:               BillableHoursMtd;
    feesBilledWTD:               BillableHoursMtd;
    feesBilledPreviousMTD:       BillableHoursMtd;
    feesBilledPreviousMYTD:       BillableHoursMtd;
    feesBilledForecast:       BillableHoursMtd;
    feesCollectedYTD:            BillableHoursMtd;
    feesCollectedMTD:            BillableHoursMtd;
    feesCollectedWTD:            BillableHoursMtd;
    feesCollectedPreviousMTD:    BillableHoursMtd;
    feesCollectedPreviousMYTD:    BillableHoursMtd;
    feesCollectedForecast:    BillableHoursMtd;
    totalDebtOwing:              Total | null;
    totalUnbilledFees:           Total | null;
    totalUnbilledDisbursements:  Total | null;
};

type BillableHoursMtd = {
    periodTotal: number;
    budget:      number;
    percentage:  number;
};

type NewMattersMtd = {
    periodTotal: number;
};

type Total = {
    periodTotal:          number;
    ninetyDaysPercentage: number;
    ninetyDaysTotal:      number;
};

const KPISummaryQuery = gql`
    query kpiSummaryData($entityType: Int, $entityGuid: String, $responsibility: Int) {
        settings {
            id
            systems {
                financialThresholdsSettings {
                    id
                    config {
                        id
                        redIndicatorWhenActualVsBudgetIsLessThanPercentage
                        amberIndicatorWhenActualVsBudgetIsLessThanPercentage
                        redIndicatorWhenNinetyDayDebtExceedsPercentage
                        redIndicatorWhenNinetyDayUnbilledFeesExceedsPercentage
                        redIndicatorWhenNinetyDayUnbilledDisbursementsExceedsPercentage
                        firmOverallStandardChargeOutRate
                        currentBankOverdraftInterestRate
                        firmStandardTradingTermsInDays
                        firmTargetWipLockupDays
                        showGreenIndicator
                    }
                }
            }
        }
        statistics {
            fees {
                kpiSummary(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    newMattersYTD {
                        periodTotal
                    }
                    newMattersMTD {
                        periodTotal
                    }
                    newMattersWTD {
                        periodTotal
                    }
                    newMattersPreviousMTD {
                        periodTotal
                    }
                    newMattersPreviousMYTD {
                        periodTotal
                    }

                    nonBillableHoursYTD {
                        periodTotal
                    }
                    nonBillableHoursMTD {
                        periodTotal
                    }
                    nonBillableHoursWTD {
                        periodTotal
                    }
                    nonBillableHoursPreviousMTD {
                        periodTotal
                    }
                    nonBillableHoursPreviousMYTD {
                        periodTotal
                    }

                    billableHoursYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    billableHoursMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    billableHoursWTD {
                        periodTotal
                        budget
                        percentage
                    }
                    billableHoursPreviousMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    billableHoursPreviousMYTD {
                        periodTotal
                        budget
                        percentage
                    }

                    feesRecordedYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesRecordedMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesRecordedWTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesRecordedPreviousMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesRecordedPreviousMYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesRecordedForecast {
                        periodTotal
                        budget
                        percentage
                    }

                    feesBilledYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesBilledMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesBilledWTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesBilledPreviousMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesBilledPreviousMYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesBilledForecast {
                        periodTotal
                        budget
                        percentage
                    }
                    feesCollectedYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesCollectedMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesCollectedWTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesCollectedPreviousMTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesCollectedPreviousMYTD {
                        periodTotal
                        budget
                        percentage
                    }
                    feesCollectedForecast {
                        periodTotal
                        budget
                        percentage
                    }
                    totalDebtOwing {
                        periodTotal
                        ninetyDaysPercentage
                        ninetyDaysTotal
                    }
                    totalUnbilledFees {
                        periodTotal
                        ninetyDaysPercentage
                        ninetyDaysTotal
                    }
                    totalUnbilledDisbursements {
                        periodTotal
                        ninetyDaysPercentage
                        ninetyDaysTotal
                    }
                }
            }
        }
    }
`;
